import { Bot, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDistanceToNow } from "date-fns";

interface SnapshotData {
  transcriptCount: number;
  reflectionCount: number;
  artifactCount: number;
  lastActivityAt: string | null;
}

interface EvidenceSnapshotSectionProps {
  snapshot: SnapshotData;
  onReviewLatestEvidence: () => void;
  onOpenTranscripts: () => void;
  onOpenEvidenceRecord: () => void;
  onGenerateCoachVeera: () => void;
  isGeneratingSenseMaking: boolean;
}

export function EvidenceSnapshotSection({
  snapshot,
  onReviewLatestEvidence,
  onOpenTranscripts,
  onOpenEvidenceRecord,
  onGenerateCoachVeera,
  isGeneratingSenseMaking,
}: EvidenceSnapshotSectionProps) {
  const formattedLastActivity = (() => {
    if (!snapshot.lastActivityAt) return "No activity";
    const date = new Date(snapshot.lastActivityAt);
    if (Number.isNaN(date.getTime())) return "No activity";
    return formatDistanceToNow(date, { addSuffix: true });
  })();

  return (
    <Card className="border-border/60">
      <CardHeader className="pb-2">
        <CardTitle className="text-h3">Evidence of Growth Snapshot</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <button type="button" onClick={onOpenTranscripts} aria-label={`Review ${snapshot.transcriptCount} transcripts`} className="rounded-lg border border-border/60 p-3 text-left transition-colors hover:border-primary/45 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
            <p className="text-xs text-muted-foreground">Transcripts</p>
            <p className="text-2xl font-semibold">{snapshot.transcriptCount}</p>
            <p className="mt-1 text-xs text-primary">Review transcripts</p>
          </button>
          <button type="button" onClick={onOpenEvidenceRecord} aria-label={`Review ${snapshot.reflectionCount} reflections`} className="rounded-lg border border-border/60 p-3 text-left transition-colors hover:border-primary/45 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
            <p className="text-xs text-muted-foreground">Reflections</p>
            <p className="text-2xl font-semibold">{snapshot.reflectionCount}</p>
            <p className="mt-1 text-xs text-primary">Open record</p>
          </button>
          <button type="button" onClick={onOpenEvidenceRecord} aria-label={`Review ${snapshot.artifactCount} artifacts`} className="rounded-lg border border-border/60 p-3 text-left transition-colors hover:border-primary/45 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
            <p className="text-xs text-muted-foreground">Artifacts</p>
            <p className="text-2xl font-semibold">{snapshot.artifactCount}</p>
            <p className="mt-1 text-xs text-primary">Open record</p>
          </button>
          <button type="button" onClick={onOpenEvidenceRecord} aria-label="Open the most recent activity" className="rounded-lg border border-border/60 p-3 text-left transition-colors hover:border-primary/45 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
            <p className="text-xs text-muted-foreground">Last activity</p>
            <p className="text-sm font-medium">{formattedLastActivity}</p>
            <p className="mt-1 text-xs text-primary">Open record</p>
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={onReviewLatestEvidence}>
            <FileText className="h-4 w-4 mr-2" />
            Find Evidence
          </Button>
          <Button
            variant="outline"
            onClick={onGenerateCoachVeera}
            disabled={isGeneratingSenseMaking}
          >
            <Bot className="h-4 w-4 mr-2" />
            {isGeneratingSenseMaking ? "Coach Veera is preparing..." : "Open Coach Veera"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
